import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { UserCog, PenLine, Stamp, ShieldCheck, Phone, Printer, Crown, Layout, UserPlus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PRIO_STYLES } from './PrioCriteriaPicker';

const PRIOS = ['A', 'B', 'C'];
const NONE = '__none__';

// Admin card (Team Management) — 22.10.2026: ONE final approver (Max) confirms every deal and reviews letters he does not
// write himself; the prio matrix only decides who writes the letter. Contact tasks: fixed owner, anyone may take over.
export function WorkflowAssignmentsCard() {
  const [members, setMembers] = useState([]);
  const [data, setData] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = () => Promise.all([api.listTeamMembers(), api.getWorkflowAssignments()])
    .then(([m, a]) => { setMembers((m.data || []).filter((u) => u.role !== 'ac_member')); setData(a.data); })
    .catch(() => {});
  useEffect(() => {
    load();
    api.getAbExperiments().then((r) => setExperiments(r.data?.experiments || [])).catch(() => {});
  }, []);

  const save = async (key, body, okMsg) => {
    setSaving(key);
    try {
      const res = await api.setWorkflowAssignments(body);
      setData(res.data);
      toast.success(okMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
      await load();
    } finally { setSaving(null); }
  };

  const memberName = (id) => members.find((m) => m.id === id)?.name || '—';
  const firstName = (u) => (u?.name || '').split(' ')[0] || '—';
  const prioRole = (p) => data?.prio_roles?.[p] || {};
  const savePrio = (p, patch, label) => save(`prio-${p}`, { prio_roles: { [p]: patch } }, `Prio ${p}: ${label}`);

  const UserSelect = ({ value, onChange, placeholder, testid, allowNone = false, disabled }) => (
    <Select value={value || (allowNone ? NONE : '')} onValueChange={(v) => onChange(v === NONE ? '' : v)} disabled={disabled}>
      <SelectTrigger className="w-full bg-secondary border-border rounded-sm h-8 text-xs" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-card border-border rounded-sm">
        {allowNone && <SelectItem value={NONE} data-testid={`${testid}-option-none`}>— Standard —</SelectItem>}
        {members.map((m) => (
          <SelectItem key={m.id} value={m.id} data-testid={`${testid}-option-${m.id}`}>{m.name}{m.role === 'admin' ? ' (Admin)' : ''}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const roleRow = (label, icon, hint, control, testId) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={testId}>
      <div className="flex items-center gap-2 sm:w-72 shrink-0">
        {icon}
        <div>
          <p className="text-sm text-foreground">{label}</p>
          <p className="text-[11px] text-muted-foreground">{hint}</p>
        </div>
      </div>
      <div className="w-full sm:w-64">{control}</div>
    </div>
  );

  if (!data) return <div className="bg-card border border-border rounded-sm p-4 text-xs text-muted-foreground" data-testid="workflow-assignments-card">Lade Workflow-Rollen…</div>;

  const final = data.final_approver;
  const finalId = final?.id;
  const variantOptions = experiments.flatMap((e) => (e.variants || []).map((v) => ({
    key: `${e.id}|${v.id}`, experiment_id: e.id, variant_id: v.id,
    label: `${v.name} · ${e.name}${e.status !== 'active' ? ` (${e.status})` : ''}`,
  })));
  const dv = data.default_variant;
  const dvKey = dv?.experiment_id && dv?.variant_id ? `${dv.experiment_id}|${dv.variant_id}` : NONE;

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5 space-y-5" data-testid="workflow-assignments-card">
      <div>
        <h3 className="font-heading text-lg text-foreground flex items-center gap-2 mb-1">
          <UserCog style={{ width: 18, height: 18 }} className="text-blue-400" /> Workflow-Rollen
        </h3>
        <p className="text-xs text-muted-foreground">
          Ein finaler Approver gibt jeden Deal frei (egal welche Prio) und prüft alle Briefe, die er nicht selbst schreibt.
          Die Deal-Prio (A/B/C) entscheidet nur noch, wer den Brief schreibt. Änderungen weisen offene Aufgaben sofort neu zu.
        </p>
      </div>

      {/* Final approver */}
      <div className="border border-blue-500/30 bg-blue-500/5 rounded-sm p-3" data-testid="final-approver-block">
        {roleRow('Finale Freigabe — Deals & Briefe', <Crown className="w-4 h-4 text-blue-400 shrink-0" />,
          'Bestätigt jeden OLTA-Approval-Vorschlag allein (darf die Prio-Kriterien dabei korrigieren) und prüft fremde Briefe vor der Produktion',
          <UserSelect value={finalId || ''} onChange={(v) => save('final_approver_id', { final_approver_id: v }, `Finale Freigabe → ${v ? memberName(v) : 'Standard (Max)'}`)}
            placeholder="Standard (Max)" allowNone disabled={saving === 'final_approver_id'} testid="assignment-final-approver-select" />,
          'final-approver-row')}
      </div>

      {/* Prio matrix: who writes */}
      <div className="overflow-x-auto -mx-1 px-1" data-testid="prio-matrix">
        <table className="w-full text-xs min-w-[560px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="text-left py-2 pr-3 font-medium">Prio</th>
              <th className="text-left py-2 pr-3 font-medium"><span className="inline-flex items-center gap-1"><PenLine className="w-3 h-3 text-emerald-400" /> Brief schreiben</span></th>
              <th className="text-left py-2 pr-3 font-medium"><span className="inline-flex items-center gap-1"><Stamp className="w-3 h-3 text-amber-400" /> Brief-Review</span></th>
              <th className="text-left py-2 font-medium"><span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-blue-400" /> Deal-Freigabe</span></th>
            </tr>
          </thead>
          <tbody>
            {PRIOS.map((p) => {
              const r = prioRole(p);
              const writerId = r.writer?.id || data.letter_drafter?.id;
              const selfReview = !!finalId && writerId === finalId;
              return (
                <tr key={p} className="border-b border-border/60 align-top" data-testid={`prio-row-${p}`}>
                  <td className="py-2.5 pr-3">
                    <span className={`inline-flex items-center text-[10px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[p]}`}>Prio {p}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="w-52">
                      <UserSelect value={r.writer?.id || ''} onChange={(v) => savePrio(p, { writer_id: v }, `Briefschreiber → ${v ? memberName(v) : 'Standard'}`)}
                        placeholder="Standard (Letter drafting)" allowNone disabled={saving === `prio-${p}`} testid={`prio-${p}-writer`} />
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-[11px]" data-testid={`prio-${p}-review`}>
                    {selfReview
                      ? <span className="text-emerald-300">entfällt — {firstName(final)} schreibt selbst, Finalisieren = Freigabe</span>
                      : <span className="text-muted-foreground">{firstName(final)} prüft, gibt frei oder schickt mit Kommentar zurück</span>}
                  </td>
                  <td className="py-2.5 text-[11px] text-muted-foreground" data-testid={`prio-${p}-approval`}>{firstName(final)} (final)</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[10px] text-muted-foreground mt-2">
          Ablauf je Deal: Vorschlag → finale Freigabe → Kontakt anlegen (Privatadresse + E-Mail + Telefon) → Brief schreiben → Brief-Review → iAuto-Produktion (Druck &amp; Versand).
        </p>
      </div>

      {/* Prio A/B letter defaults */}
      <div className="border border-border rounded-sm p-3 space-y-2" data-testid="prio-letter-defaults-block">
        {roleRow('Standard-Variante für Prio A/B Briefe', <Layout className="w-4 h-4 text-violet-400 shrink-0" />,
          'Landing Page neuer Prio-A/B-Briefe bekommt diese A/B-Variante (z.B. „mit Shareholder“) + handschriftlicher Zusatz — pro Brief weiter änderbar',
          <Select value={dvKey} disabled={saving === 'default_variant'}
            onValueChange={(v) => {
              const opt = variantOptions.find((o) => o.key === v);
              save('default_variant', { default_variant: opt ? { experiment_id: opt.experiment_id, variant_id: opt.variant_id } : null },
                opt ? `Standard-Variante → ${opt.label}` : 'Standard-Variante entfernt — A/B-Zuteilung läuft automatisch');
            }}>
            <SelectTrigger className="w-full bg-secondary border-border rounded-sm h-8 text-xs" data-testid="assignment-default-variant-select">
              <SelectValue placeholder="keine (automatische A/B-Zuteilung)" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value={NONE} data-testid="assignment-default-variant-option-none">— keine (automatische A/B-Zuteilung) —</SelectItem>
              {variantOptions.map((o) => (
                <SelectItem key={o.key} value={o.key} data-testid={`assignment-default-variant-option-${o.variant_id}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>,
          'default-variant-row')}
        {dv && dv.valid === false && (
          <p className="text-[10px] text-red-300" data-testid="default-variant-invalid">Die gespeicherte Variante existiert nicht mehr — bitte neu wählen.</p>
        )}
        {dv && dv.experiment_status && dv.experiment_status !== 'active' && (
          <p className="text-[10px] text-amber-300" data-testid="default-variant-inactive">Experiment „{dv.experiment_name}“ ist {dv.experiment_status} — die Variante wird erst wieder gesetzt, wenn es aktiv ist.</p>
        )}
      </div>

      {/* Fixed roles */}
      <div className="space-y-3 pt-1 border-t border-border">
        {roleRow('Kontakt anlegen (Privatadresse + E-Mail + Telefon)', <Phone className="w-4 h-4 text-sky-400 shrink-0" />,
          'Nach jeder Deal-Freigabe; nicht abhakbar, bis alle drei Angaben da sind. Jedes Teammitglied kann die Aufgabe übernehmen',
          <UserSelect value={data.enricher?.id || ''} onChange={(v) => save('enricher_id', { enricher_id: v }, `Kontakt anlegen → ${v ? memberName(v) : 'Briefschreiber'}`)}
            placeholder="niemand → Briefschreiber" allowNone disabled={saving === 'enricher_id'} testid="assignment-enricher-select" />,
          'enricher-row')}
        <p className="text-[10px] text-muted-foreground flex items-center gap-1 -mt-1 sm:ml-6"><UserPlus className="w-3 h-3" /> „Übernehmen“-Button in Meine Aufgaben und auf der Deal-Seite — der bisherige Zuständige wird per Push informiert.</p>
        {roleRow('Letter drafting (Standard)', <PenLine className="w-4 h-4 text-emerald-400 shrink-0" />,
          'Schreibt Briefe, wenn für die Prio niemand gesetzt ist / Deal ohne Prio',
          <UserSelect value={data.letter_drafter?.id || ''} onChange={(v) => save('letter_drafter_id', { letter_drafter_id: v }, `Letter drafting → ${memberName(v)}`)}
            placeholder="Select a team member" disabled={saving === 'letter_drafter_id'} testid="assignment-drafter-select" />,
          'drafter-row')}
        {roleRow('Produktion & Versand', <Printer className="w-4 h-4 text-amber-400 shrink-0" />,
          'iAuto Workspace: Druck, Umschläge, Versand freigegebener Briefe',
          <UserSelect value={data.letter_approver?.id || ''} onChange={(v) => save('letter_approver_id', { letter_approver_id: v }, `Produktion & Versand → ${memberName(v)}`)}
            placeholder="Select a team member" disabled={saving === 'letter_approver_id'} testid="assignment-approver-select" />,
          'production-row')}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="deal-approvers-row">
          <div className="flex items-center gap-2 sm:w-72 shrink-0">
            <ShieldCheck className="w-4 h-4 text-blue-400 shrink-0" />
            <div>
              <p className="text-sm text-foreground">Pass-, Contact- & Skip-Bestätigung (Vier-Augen)</p>
              <p className="text-[11px] text-muted-foreground">Bestätigen Pass-Vorschläge, Contact-Moves und Outreach-Skips — genau zwei Personen (nicht die OLTA-Freigabe)</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {[0, 1].map((idx) => {
              const das = (data.deal_approvers || []).map((u) => u.id);
              return (
                <div key={idx} className="w-full sm:w-56">
                  <UserSelect value={das[idx] || ''} disabled={saving === 'deal_approvers'} testid={`assignment-deal-approver-${idx + 1}-select`} placeholder={`Approver ${idx + 1}`}
                    onChange={(v) => {
                      const next = [...das]; next[idx] = v;
                      if (!next[0] || !next[1]) return;
                      if (next[0] === next[1]) { toast.error('Deal approvers must be two different people'); return; }
                      save('deal_approvers', { deal_approver_ids: next }, `Deal approvers → ${next.map(memberName).join(' + ')}`);
                    }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
